/**
 * useCostSummary — fetch /api/insights/cost/ for a non-default period.
 * Pass null while the card shows the default (current-month) view, which
 * renders from the dashboard payload without an extra request.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { insights as insightsApi } from '@/helpers/ApiHelper';
import type { CostSummary } from '@/types/insights';
import type { Period } from '@/pages/insights/period';

interface UseCostSummaryResult {
  data: CostSummary | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCostSummary(period: Period | null): UseCostSummaryResult {
  const [data, setData] = useState<CostSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Monotonically-increasing request id shared by the effect fetch AND the
  // manual `refetch`. Every new request (automatic — a period change — or
  // manual — a Retry click) bumps this ref and captures its own id; a
  // response is only applied if the ref still matches the id it captured
  // when the request started. This is the single guard for "is this
  // response still the one we care about" across BOTH fetch paths — without
  // it, a manual Retry for an old period can resolve after a newer period
  // change and clobber the state that's actually on screen.
  const requestIdRef = useRef(0);

  const runFetch = useCallback(async (p: Period) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await insightsApi.costSummary(p.from, p.to);
      if (requestIdRef.current === requestId) {
        setData(result);
      }
    } catch (e) {
      if (requestIdRef.current === requestId) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, []);

  const refetch = useCallback(async () => {
    if (period === null) {
      requestIdRef.current += 1; // invalidate any still-in-flight request
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    await runFetch(period);
  }, [period, runFetch]);

  useEffect(() => {
    if (period === null) {
      requestIdRef.current += 1; // invalidate any still-in-flight request
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    void runFetch(period);
  }, [period, runFetch]);

  return { data, isLoading, error, refetch };
}
